/**
 * LeadSourceBadge - Exibe origem do lead com ícone Lucide
 * Componente reutilizável para todo o CRM
 * 
 * RELEASE: 2026-01-12
 * Versão: 1.0.0
 * - Criação inicial com suporte a variantes default e inline
 * - Integração com LEAD_SOURCE_ICONS e getLeadSourceLabel
 */
import React from 'react';
import { cn } from '@/lib/utils';
import { getLeadSourceLabel, getLeadSourceIcon } from '@/lib/lead-sources';

/**
 * Props do componente LeadSourceBadge
 */
interface LeadSourceBadgeProps {
  /** ID da origem do lead (ex: 'whatsapp', 'instagram') */
  sourceId: string | null | undefined;
  /** Variante visual: default (com background) ou inline (apenas texto) */
  variant?: 'default' | 'inline';
  /** Tamanho do badge */
  size?: 'xs' | 'sm' | 'md';
  /** Classes CSS adicionais */
  className?: string;
  /** Exibir ou ocultar o label textual */
  showLabel?: boolean;
}

/**
 * Configuração de tamanhos para o badge
 */
const sizeConfig = {
  xs: {
    container: 'text-[9px] px-1 py-0.5 gap-0.5',
    icon: 'h-2.5 w-2.5',
  },
  sm: {
    container: 'text-[10px] px-1.5 py-0.5 gap-1',
    icon: 'h-3 w-3',
  },
  md: {
    container: 'text-xs px-2 py-1 gap-1.5',
    icon: 'h-3.5 w-3.5',
  },
};

/**
 * Componente LeadSourceBadge
 * Exibe a origem do lead com ícone visual e label opcional
 * 
 * @example
 * // Badge padrão com background
 * <LeadSourceBadge sourceId="whatsapp" />
 * 
 * // Badge inline (sem background)
 * <LeadSourceBadge sourceId="instagram" variant="inline" />
 * 
 * // Apenas ícone, sem label
 * <LeadSourceBadge sourceId="facebook" showLabel={false} />
 */
export function LeadSourceBadge({
  sourceId,
  variant = 'default',
  size = 'sm',
  className,
  showLabel = true,
}: LeadSourceBadgeProps) {
  // Retorna null se não houver sourceId
  if (!sourceId) return null;

  // Obtém ícone e label da origem
  const IconComponent = getLeadSourceIcon(sourceId);
  const label = getLeadSourceLabel(sourceId);
  const config = sizeConfig[size];

  // Variante inline: apenas texto e ícone, sem background
  if (variant === 'inline') {
    return (
      <span className={cn(
        'inline-flex items-center text-muted-foreground',
        config.container,
        className
      )}>
        {IconComponent && (
          <IconComponent className={cn(config.icon, 'shrink-0')} />
        )}
        {showLabel && <span className="truncate">{label}</span>}
      </span>
    );
  }

  // Variante default: com background
  return (
    <span className={cn(
      'inline-flex items-center bg-muted rounded truncate',
      config.container,
      className
    )}>
      {IconComponent && (
        <IconComponent className={cn(config.icon, 'text-muted-foreground shrink-0')} />
      )}
      {showLabel && <span className="truncate">{label}</span>}
    </span>
  );
}
